import React, { useState, useRef, useEffect } from 'react';
import { Box } from '@mui/material';

import { E_COMMERCE_STYLES } from 'components/ECommerce/config';
import YouTube, { YouTubeProps } from 'react-youtube';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';

export interface Reel {
    id: number;
    thumbnail: string;
    videoUrl: string;
    title: string;
    views?: string;
}

const ShortsPlayer = ({ videoUrl, title, thumbnail, isCurrent, onPlay, sx }: { videoUrl: string, title: string, thumbnail?: string, isCurrent: boolean, onPlay: () => void, sx?: any }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [started, setStarted] = useState(false);
    const playerRef = useRef<any>(null);

    const getVideoId = (url: string) => {
        const match = url.match(/\/shorts\/([^/?#&]+)/) || url.match(/\/embed\/([^/?#&]+)/) || url.match(/v=([^&]+)/);
        return match ? match[1] : '';
    };

    const videoId = React.useMemo(() => getVideoId(videoUrl), [videoUrl]);
    const thumbUrl = thumbnail || `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;

    if (!videoId) return null;

    const onPlayerReady: YouTubeProps['onReady'] = (event) => {
        playerRef.current = event.target;
        if (isCurrent && started) {
            event.target.unMute();
            event.target.setVolume(100);
            event.target.playVideo();
        } else {
            event.target.pauseVideo();
        }
    };

    // Effect to handle pausing when another video becomes current
    useEffect(() => {
        if (!isCurrent && playerRef.current) {
            playerRef.current.pauseVideo();
            setIsPlaying(false);
        } else if (isCurrent && playerRef.current && started) {
            playerRef.current.unMute();
            playerRef.current.playVideo();
            setIsPlaying(true);
        }
    }, [isCurrent, started]);

    const togglePlay = () => {
        if (!started) {
            setStarted(true);
            onPlay();
            return;
        }

        if (!playerRef.current) return;

        if (isPlaying) {
            playerRef.current.pauseVideo();
            setIsPlaying(false);
        } else {
            onPlay(); // Notify parent we are playing
            playerRef.current.unMute();
            playerRef.current.playVideo();
            setIsPlaying(true);
        }
    };

    const onPlayerStateChange: YouTubeProps['onStateChange'] = (event) => {
        if (event.data === 1) setIsPlaying(true); // Playing
        if (event.data === 2) setIsPlaying(false); // Paused
        if (event.data === 0) setIsPlaying(false); // Ended
    };

    const opts: YouTubeProps['opts'] = React.useMemo(() => ({
        height: '100%',
        width: '100%',
        playerVars: {
            autoplay: 1,
            controls: 0,
            rel: 0,
            modestbranding: 1,
            loop: 1,
            fs: 0,
            iv_load_policy: 3,
            playlist: videoId,
            playsinline: 1,
            origin: typeof window !== 'undefined' ? window.location.origin : undefined,
        },
    }), [videoId]);

    return (
        <Box
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            sx={{
                position: 'relative',
                width: '100%',
                aspectRatio: '9/16',
                borderRadius: '12px',
                overflow: 'hidden',
                bgcolor: '#000',
                ...sx
            }}
        >
            {!started ? (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundImage: `url(${thumbUrl})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        zIndex: 1,
                    }}
                />
            ) : (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        // Pointer events logic for custom overlay
                        '& iframe': {
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            pointerEvents: 'none',
                        }
                    }}
                >
                    <YouTube
                        videoId={videoId}
                        opts={opts}
                        onReady={onPlayerReady}
                        onStateChange={onPlayerStateChange}
                        style={{ width: '100%', height: '100%' }}
                    />
                </Box>
            )}

            {/* Custom Control Overlay */}
            <Box
                onClick={togglePlay}
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    zIndex: 2,
                    background: isPlaying ? 'transparent' : 'rgba(0,0,0,0.2)',
                    transition: 'background 0.3s',
                }}
            >
                <Box
                    sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        bgcolor: 'rgba(0,0,0,0.4)',
                        backdropFilter: 'blur(4px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid rgba(255,255,255,0.2)',
                        opacity: !isPlaying || isHovered ? 1 : 0,
                        transform: !isPlaying || isHovered ? 'scale(1)' : 'scale(0.8)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                >
                    {isPlaying ? (
                        <PauseIcon sx={{ color: '#fff', fontSize: 32 }} />
                    ) : (
                        <PlayArrowIcon sx={{ color: '#fff', fontSize: 32 }} />
                    )}
                </Box>
            </Box>
        </Box>
    );
};


const OsperbShorts = ({
    reels,
    itemStyle,
    height,
    visibleItems
}: {
    reels: any[],
    itemStyle?: { width?: string | number, height?: string | number },
    height?: string | number,
    visibleItems?: { xs?: number, sm?: number, md?: number, lg?: number }
}) => {
    // Sample YouTube Shorts data

    const paddedReels = React.useMemo(() => [...reels, ...reels, ...reels], [reels]);
    const scrollRef = useRef<HTMLDivElement>(null);
    const [playingIndex, setPlayingIndex] = useState<number | null>(null);

    // Handle Infinite Scroll Logic
    const handleScroll = () => {
        if (!scrollRef.current) return;
        const { scrollLeft, scrollWidth } = scrollRef.current;
        const oneSetWidth = scrollWidth / 3;

        // Tolerance buffer to prevent jitter
        const buffer = 10;

        // If we scroll into the first set (too far left), jump to the middle set
        if (scrollLeft < buffer) {
            scrollRef.current.scrollLeft += oneSetWidth;
        }
        // If we scroll into the third set (too far right), jump back to the middle set
        else if (scrollLeft >= oneSetWidth * 2 - buffer) {
            scrollRef.current.scrollLeft -= oneSetWidth;
        }
    };

    // Initialize scroll position to the middle set
    useEffect(() => {
        if (scrollRef.current) {
            const oneSetWidth = scrollRef.current.scrollWidth / 3;
            // Center the scroll view initially
            scrollRef.current.scrollLeft = oneSetWidth;
        }
    }, [reels]);

    return (
        <Box
            sx={{
                mx: -3,
                padding: '0',
                position: 'relative',
                overflow: 'hidden',
                pb: 2
            }}
        >
            {/* Shorts Scroll View */}
            <Box
                ref={scrollRef}
                onScroll={handleScroll}
                sx={{
                    maxWidth: E_COMMERCE_STYLES.container.maxWidthWeb,
                    margin: E_COMMERCE_STYLES.container.mx,
                    display: 'flex',
                    overflowX: 'auto',
                    gap: 2,
                    pb: 2,
                    px: 2,
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': {
                        display: 'none'
                    },
                    // scrollSnapType: 'x mandatory', 
                    // Snapping disabled to ensure smooth infinite loop illusion
                }}
            >
                {paddedReels.map((reel, index) => (
                    <Box
                        key={`${reel.id}-${index}`}
                        title={reel?.title}
                        sx={{
                            minWidth: itemStyle?.width ?? {
                                xs: visibleItems?.xs ? `${100 / visibleItems.xs}vw` : '60vw', // ~1.5 items visible on mobile
                                sm: visibleItems?.sm ? `${100 / visibleItems.sm}vw` : '40vw', // ~2.5 items visible on tablet
                                md: visibleItems?.md ? `${100 / visibleItems.md}vw` : '22vw', // ~4.5 items visible on desktop
                                lg: visibleItems?.lg ? `${100 / visibleItems.lg}vw` : undefined,
                            },
                            height: height || itemStyle?.height,
                            ...itemStyle,
                            scrollSnapAlign: 'start',
                            flexShrink: 0,
                        }}
                    >
                        <ShortsPlayer
                            videoUrl={reel.videoUrl}
                            title={reel.title}
                            thumbnail={reel.thumbnail}
                            isCurrent={playingIndex === index}
                            onPlay={() => setPlayingIndex(index)}
                            sx={(height || itemStyle?.height) ? { aspectRatio: 'unset', height: '100%' } : undefined}
                        />
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

export default OsperbShorts;
